import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  User, CreditCard, Landmark, Stethoscope, FileText,
  LogOut, Check, X, Loader2, GraduationCap,
} from "lucide-react";
import { API } from "../lib/api";
import { WORKMAN_LANGS, makeT } from "../lib/workmanI18n";
import { DigitalIdCard } from "../components/DigitalIdCard";
import { printScreeningForm, printFormXI, printIdentityCard } from "../components/WorkmenPrintables";

const NCC_LOGO = "/ncc-logo.png";

const SECTIONS = [
  { key: "idcard", icon: CreditCard },
  { key: "profile", icon: User },
  { key: "health", icon: Stethoscope },
  { key: "bank", icon: Landmark },
  { key: "skills", icon: GraduationCap },
];

const mask = (v, keep = 4) => (v ? "•".repeat(Math.max(0, String(v).length - keep)) + String(v).slice(-keep) : "—");

function wapi() {
  const t = localStorage.getItem("workman_token");
  return axios.create({ baseURL: API, headers: { Authorization: `Bearer ${t}` } });
}

export default function WorkmanPortalPage() {
  const nav = useNavigate();
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("idcard");
  const [lang, setLang] = useState(localStorage.getItem("workman_lang") || "en");
  const t = makeT(lang);
  function changeLang(code) {
    localStorage.setItem("workman_lang", code);
    setLang(code);
  }

  useEffect(() => {
    if (!localStorage.getItem("workman_token")) { nav("/admin/login"); return; }
    wapi().get("/workman-auth/me")
      .then((r) => setW(r.data))
      .catch(() => { localStorage.removeItem("workman_token"); nav("/admin/login"); })
      .finally(() => setLoading(false));
  }, [nav]);

  function logout() {
    localStorage.removeItem("workman_token");
    localStorage.removeItem("workman_id");
    localStorage.removeItem("workman_prev_login");
    nav("/admin/login");
  }

  if (loading) return <div className="grid min-h-screen place-items-center bg-[#f5f7fc]"><Loader2 className="h-8 w-8 animate-spin text-ncc-navy" /></div>;
  if (!w) return null;

  return (
    <div className="min-h-screen bg-[#f5f7fc]" data-testid="workman-portal">
      <header className="sticky top-0 z-20 border-b-4 border-ncc-gold bg-ncc-navy px-4 py-3">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={NCC_LOGO} alt="SWMS-MPDB" className="h-9 w-9 rounded bg-white p-0.5" />
            <div>
              <div className="font-heading text-sm font-black text-white">{t("portal")}</div>
              <div className="font-mono text-[10px] text-blue-200">{w.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={lang}
              onChange={(e) => changeLang(e.target.value)}
              data-testid="workman-lang-select"
              title={t("language")}
              className="max-w-[110px] border-2 border-white/40 bg-ncc-navy px-2 py-1.5 text-xs font-bold text-white focus:border-ncc-gold focus:outline-none"
            >
              {WORKMAN_LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <button onClick={logout} data-testid="workman-logout-btn" className="inline-flex items-center gap-1.5 border-2 border-ncc-gold bg-ncc-gold px-3 py-1.5 text-xs font-bold text-ncc-navy hover:brightness-110">
              <LogOut className="h-3.5 w-3.5" /> {t("logout")}
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-3 py-4 md:flex-row">
        <nav className="flex shrink-0 gap-1 overflow-x-auto pb-1 md:w-56 md:flex-col md:overflow-visible">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                data-testid={`workman-nav-${s.key}`}
                className={`flex shrink-0 items-center gap-2 border-2 px-3 py-2 text-left text-xs font-bold tracking-wide transition-colors ${section === s.key ? "border-ncc-navy bg-ncc-navy text-white" : "border-slate-200 bg-white text-slate-700 hover:border-ncc-navy"}`}
              >
                <Icon className="h-4 w-4" /> {t(s.key)}
              </button>
            );
          })}
        </nav>

        <main className="min-w-0 flex-1">
          {section === "idcard" && <IdCardSection w={w} />}
          {section === "profile" && (
            <>
              <HomeSection w={w} />
              <ProfileSection w={w} onSaved={setW} />
              <DocumentsSection w={w} />
              <ReadySection w={w} t={t} />
            </>
          )}
          {section === "health" && (
            <>
              <MedicalSection w={w} />
              <InductionSection w={w} />
              <SafetySection w={w} />
            </>
          )}
          {section === "bank" && <BankSection w={w} onSaved={setW} />}
          {section === "skills" && <SkillsSection w={w} onSaved={setW} />}
        </main>
      </div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <section className="mb-4 border-2 border-slate-900 bg-white" data-testid={testid}>
      <div className="border-b-2 border-slate-900 bg-slate-50 px-4 py-2 font-heading text-sm font-black uppercase tracking-wider text-ncc-navy">{title}</div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-3 border-b border-slate-100 py-1.5 text-sm" data-testid={testid}>
      <span className="shrink-0 font-mono text-[11px] uppercase tracking-wide text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-900">{value ?? "—"}</span>
    </div>
  );
}

function StatusChip({ ok, yes = "DONE", no = "PENDING" }) {
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 font-mono text-[11px] font-bold ${ok ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}>
      {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />} {ok ? yes : no}
    </span>
  );
}

const medicalDone = (w) => !!(w.vision || w.hearing || w.phys_exam_blood_pressure || w.phys_exam_pulse || w.physical_deformities || w.medical_history);
const bankDone = (w) => !!(w.bank_name && w.bank_account_no && w.bank_ifsc);

function HomeSection({ w }) {
  const prev = (() => { try { return JSON.parse(localStorage.getItem("workman_prev_login") || "null"); } catch { return null; } })();
  return (
    <>
      <Card title={`👋 Welcome, ${w.name}`} testid="workman-home">
        <div className="grid gap-x-8 sm:grid-cols-2">
          <Row label="ID No" value={w.id_card_no || "Not issued yet"} testid="workman-home-idno" />
          <Row label="Name" value={w.name} />
          <Row label="Trade" value={w.trade} />
          <Row label="Project / Site" value={w.ncc_site_name} />
          <Row label="Contractor" value={w.contractor_name} />
          <Row label="Status" value={<StatusChip ok={w.status === "approved"} yes="APPROVED" no={(w.status || "pending").toUpperCase()} />} />
        </div>
        {prev?.at && (
          <p className="mt-3 font-mono text-[11px] text-slate-500" data-testid="workman-last-login">
            Last login: {new Date(prev.at).toLocaleString()} {prev.device ? `· ${prev.device.slice(0, 60)}` : ""}
          </p>
        )}
      </Card>
      <Card title="📋 Dashboard Summary">
        <div className="grid gap-2 sm:grid-cols-2">
          <Row label="Medical" value={<StatusChip ok={w.medical_status === "approved"} yes="APPROVED" />} />
          <Row label="Safety Induction" value={<StatusChip ok={w.status === "approved"} yes="CLEARED" />} />
          <Row label="Bank Details" value={<StatusChip ok={bankDone(w)} yes="ADDED" no="NOT ADDED" />} />
          <Row label="ID Card" value={<StatusChip ok={!!w.id_card_no} yes="GENERATED" no="NOT ISSUED" />} />
          <Row label="Gate Pass / Active" value={<StatusChip ok={w.is_active !== false} yes="ACTIVE" no="INACTIVE" />} />
          <Row label="Documents" value={`${(w.certificates || []).length} file(s)`} />
        </div>
      </Card>
    </>
  );
}

function ProfileSection({ w, onSaved }) {
  const [stat, setStat] = useState({ pf_no: w.pf_no || "", uan_no: w.uan_no || "", esic_no: w.esic_no || "" });
  const [busy, setBusy] = useState(false);
  async function saveStat() {
    setBusy(true);
    try {
      const r = await wapi().put("/workman-auth/me", stat);
      onSaved(r.data);
      toast.success("Statutory numbers saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  }
  return (
    <>
      <Card title="👤 Personal Information" testid="workman-profile">
        <div className="mb-3 flex items-center gap-4">
          {w.photo_base64 ? (
            <img src={w.photo_base64} alt={w.name} className="h-20 w-20 border-2 border-slate-900 object-cover" />
          ) : (
            <div className="grid h-20 w-20 place-items-center border-2 border-slate-900 bg-slate-100 font-heading text-2xl font-black text-slate-400">{(w.name || "?")[0]}</div>
          )}
          <div>
            <div className="font-heading text-lg font-black text-slate-900">{w.name}</div>
            <div className="font-mono text-xs text-slate-500">{w.trade || "—"} · {w.contractor_name || "—"}</div>
          </div>
        </div>
        <div className="grid gap-x-8 sm:grid-cols-2">
          <Row label="Workman ID" value={w.id_card_no || w.id?.slice(0, 8).toUpperCase()} />
          <Row label="Father's / Husband's Name" value={w.father_husband_name} />
          <Row label="Date of Birth" value={w.date_of_birth} />
          <Row label="Age" value={w.age_years != null ? `${w.age_years} yrs` : null} />
          <Row label="Gender" value={w.gender} />
          <Row label="Blood Group" value={w.blood_group} />
          <Row label="Marital Status" value={w.marital_status} />
          <Row label="Mobile Number" value={w.mobile} />
          <Row label="Alternate Mobile" value={w.alternate_mobile} />
          <Row label="Aadhaar (Masked)" value={w.aadhaar ? mask(w.aadhaar) : null} />
          <Row label="ID Proof" value={w.id_proof_type ? `${w.id_proof_type} · ${mask(w.id_proof_number)}` : null} />
          <Row label="Email" value={w.email} />
        </div>
        <div className="mt-4 border-t-2 border-slate-100 pt-3">
          <div className="font-mono text-[11px] uppercase tracking-wide text-slate-500">Statutory Numbers (self entry)</div>
          <div className="mt-2 grid gap-3 sm:grid-cols-3">
            {[["pf_no", "PF Number"], ["uan_no", "UAN"], ["esic_no", "ESIC Number"]].map(([k, label]) => (
              <label key={k} className="block">
                <span className="font-mono text-[11px] uppercase tracking-wide text-slate-500">{label}</span>
                <input
                  value={stat[k]}
                  onChange={(e) => setStat({ ...stat, [k]: e.target.value })}
                  data-testid={`workman-profile-${k}`}
                  className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-ncc-navy focus:outline-none"
                />
              </label>
            ))}
          </div>
          <button onClick={saveStat} disabled={busy} data-testid="workman-profile-stat-save" className="mt-3 inline-flex items-center gap-2 border-2 border-ncc-navy bg-ncc-navy px-5 py-2 text-xs font-bold text-white hover:brightness-110 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} SAVE
          </button>
        </div>
      </Card>
      <Card title="🏠 Address">
        <Row label="Permanent Address" value={w.address} />
        <Row label="Present Address" value={w.present_address} />
        <Row label="Village" value={w.village} />
        <Row label="District / City" value={w.city} />
        <Row label="State" value={w.state} />
        <Row label="PIN Code" value={w.pincode} />
      </Card>
      <Card title="🚨 Emergency Contact">
        <Row label="Contact Person" value={w.emergency_contact_name} />
        <Row label="Relationship" value={w.emergency_contact_relation} />
        <Row label="Mobile Number" value={w.emergency_contact_number} />
      </Card>
    </>
  );
}

function IdCardSection({ w }) {
  return (
    <Card title="🆔 Digital ID Card" testid="workman-idcard">
      <DigitalIdCard
        testid="workman-digital-id"
        d={{
          id: w.id,
          name: w.name,
          idNo: w.id_card_no,
          trade: w.trade,
          mobile: w.mobile,
          site: w.ncc_site_name,
          contractorName: w.contractor_name,
          uan: w.uan_no,
          esic: w.esic_no,
          bloodGroup: w.blood_group,
          photo: w.photo_base64,
          active: w.is_active !== false,
          status: w.status,
          issuedAt: w.id_card_issued_at,
          cardTitle: "WORKMAN IDENTITY CARD",
        }}
      />
      {!w.id_card_no && <p className="mt-3 text-center font-mono text-[11px] text-amber-700">ID card number not issued yet — it will appear here once admin issues your card.</p>}
    </Card>
  );
}

function BankSection({ w, onSaved }) {
  const [form, setForm] = useState({
    bank_name: w.bank_name || "", bank_branch: w.bank_branch || "", bank_ifsc: w.bank_ifsc || "",
    bank_account_name: w.bank_account_name || "", bank_account_no: w.bank_account_no || "",
    bank_upi_id: w.bank_upi_id || "", pf_no: w.pf_no || "", uan_no: w.uan_no || "", esic_no: w.esic_no || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function save() {
    setBusy(true);
    try {
      const r = await wapi().put("/workman-auth/me", form);
      onSaved(r.data);
      toast.success("Details saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  }

  const F = ({ label, k, placeholder }) => (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-wide text-slate-500">{label}</span>
      <input value={form[k]} onChange={set(k)} placeholder={placeholder || ""} data-testid={`workman-bank-${k}`}
        className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-ncc-navy focus:outline-none" />
    </label>
  );

  return (
    <Card title="🏦 Bank Details & Statutory Numbers (Self Entry)" testid="workman-bank">
      {w.bank_account_no && (
        <p className="mb-3 font-mono text-[11px] text-slate-500">Saved account: <b>{mask(w.bank_account_no)}</b> · {w.bank_name} {w.bank_ifsc ? `· ${w.bank_ifsc}` : ""}</p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <F label="Bank Name" k="bank_name" />
        <F label="Branch" k="bank_branch" />
        <F label="IFSC" k="bank_ifsc" />
        <F label="Account Holder Name" k="bank_account_name" />
        <F label="Account Number" k="bank_account_no" />
        <F label="UPI ID (optional)" k="bank_upi_id" />
        <F label="PF Number (optional)" k="pf_no" />
        <F label="UAN (optional)" k="uan_no" />
        <F label="ESIC Number (optional)" k="esic_no" />
      </div>
      <button onClick={save} disabled={busy} data-testid="workman-bank-save" className="mt-4 inline-flex items-center gap-2 border-2 border-ncc-navy bg-ncc-navy px-5 py-2 text-xs font-bold text-white hover:brightness-110 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} SAVE DETAILS
      </button>
    </Card>
  );
}

function _workmanContractor(w) {
  return {
    name: w.contractor_name || "",
    contact_phone: w.contractor_phone || "",
    ncc_site_name: w.ncc_site_name || "",
    is_payroll: !!w.is_payroll,
    source: w.source,
    source_agency_name: w.source_agency_name,
  };
}

function DocPrintBar({ w, docs }) {
  const c = _workmanContractor(w);
  const map = {
    medical: { label: "Medical (Screening)", icon: Stethoscope, fn: () => printScreeningForm(w, c) },
    induction: { label: "Induction (Form-XI)", icon: FileText, fn: () => printFormXI(w, c) },
    idcard: { label: "Identity Card", icon: CreditCard, fn: () => printIdentityCard(w, c, null) },
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3" data-testid="workman-doc-print-bar">
      {docs.map((k) => {
        const d = map[k];
        const Icon = d.icon;
        return (
          <button
            key={k}
            type="button"
            onClick={d.fn}
            data-testid={`workman-print-${k}`}
            className="inline-flex items-center gap-1.5 border-2 border-ncc-navy bg-white px-3 py-2 industrial-label text-[11px] text-ncc-navy transition-all hover:-translate-y-0.5 hover:bg-ncc-tint"
          >
            <Icon className="h-3.5 w-3.5" /> View / Print {d.label}
          </button>
        );
      })}
    </div>
  );
}

function MedicalSection({ w }) {
  return (
    <Card title="🩺 Medical Status" testid="workman-medical">
      <Row label="Medical Fitness" value={<StatusChip ok={medicalDone(w)} yes="DATA CAPTURED" no="NOT CAPTURED" />} />
      <Row label="Medical Approval (Stage 1)" value={<StatusChip ok={w.medical_status === "approved"} yes="APPROVED (FIT)" no="AWAITING MEDICAL OFFICER" />} />
      <Row label="Medical Approval Date" value={w.medical_approved_at ? new Date(w.medical_approved_at).toLocaleDateString() : null} />
      <Row label="Blood Group" value={w.blood_group} />
      <Row label="Vision Test" value={w.vision} />
      <Row label="Hearing Test" value={w.hearing} />
      <Row label="Blood Pressure" value={w.phys_exam_blood_pressure} />
      <Row label="Pulse" value={w.phys_exam_pulse} />
      <Row label="Medical History" value={w.medical_history} />
      <Row label="Physical Deformities" value={w.physical_deformities} />
      <DocPrintBar w={w} docs={["medical"]} />
    </Card>
  );
}

function InductionSection({ w }) {
  return (
    <Card title="🦺 Safety Induction" testid="workman-induction">
      <Row label="Medical Clearance (Stage 1)" value={<StatusChip ok={w.medical_status === "approved"} yes="APPROVED" no="PENDING" />} />
      <Row label="Induction Status (Stage 2)" value={<StatusChip ok={w.status === "approved"} yes="CLEARED" no="PENDING" />} />
      <Row label="Induction Date" value={w.approved_at ? new Date(w.approved_at).toLocaleDateString() : null} />
      <Row label="Approved By" value={w.approved_by_name} />
      {w.status === "rejected" && <Row label="Rejection Reason" value={w.rejection_reason} />}
      <p className="mt-3 font-mono text-[11px] text-slate-500">Safety induction is conducted at your NCC site after Medical approval. Contact your contractor or the site safety officer for schedules.</p>
      <DocPrintBar w={w} docs={["induction", "idcard"]} />
    </Card>
  );
}

function DocumentsSection({ w }) {
  const docs = w.certificates || [];
  return (
    <Card title="📄 Documents & Certificates" testid="workman-documents">
      <Row label="Photo" value={<StatusChip ok={!!w.photo_base64} yes="UPLOADED" no="MISSING" />} />
      <Row label="Aadhaar / ID Proof" value={<StatusChip ok={!!(w.aadhaar || w.id_proof_number)} yes="ON RECORD" no="MISSING" />} />
      <Row label="Certificates on file" value={`${docs.length} file(s)`} />
      {docs.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
          {docs.map((c, i) => (
            <a key={i} href={c} target="_blank" rel="noreferrer" className="grid h-20 place-items-center border-2 border-slate-300 bg-slate-50 font-mono text-[10px] text-slate-600 hover:border-ncc-navy">
              <FileText className="h-6 w-6" /> Doc {i + 1}
            </a>
          ))}
        </div>
      )}
      <p className="mt-3 font-mono text-[11px] text-slate-500">To add or update documents, contact your contractor — they upload documents through their portal.</p>
    </Card>
  );
}

function SkillsSection({ w, onSaved }) {
  const [form, setForm] = useState({ secondary_trade: w.secondary_trade || "", skill_level: w.skill_level || "" });
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      const r = await wapi().put("/workman-auth/me", form);
      onSaved(r.data);
      toast.success("Skill profile saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  }
  const transfers = w.transfer_history || [];
  return (
    <>
      <Card title="🎓 Skill Profile" testid="workman-skills">
        <Row label="Primary Trade" value={w.trade} />
        <Row label="Experience (Years)" value={w.experience_years != null ? `${w.experience_years} yrs` : null} />
        <Row label="Certifications on file" value={`${(w.certificates || []).length} file(s)`} />
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="font-mono text-[11px] uppercase text-slate-500">Secondary Trade (self entry)</span>
            <input value={form.secondary_trade} onChange={(e) => setForm({ ...form, secondary_trade: e.target.value })} data-testid="workman-skill-secondary"
              className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-ncc-navy focus:outline-none" />
          </label>
          <label className="block">
            <span className="font-mono text-[11px] uppercase text-slate-500">Skill Level</span>
            <select value={form.skill_level} onChange={(e) => setForm({ ...form, skill_level: e.target.value })} data-testid="workman-skill-level"
              className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-ncc-navy focus:outline-none">
              <option value="">Select…</option>
              <option>Helper</option>
              <option>Semi-Skilled</option>
              <option>Skilled</option>
              <option>Highly Skilled</option>
              <option>Supervisor</option>
            </select>
          </label>
        </div>
        <button onClick={save} disabled={busy} data-testid="workman-skill-save" className="mt-3 inline-flex items-center gap-2 border-2 border-ncc-navy bg-ncc-navy px-5 py-2 text-xs font-bold text-white hover:brightness-110 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} SAVE
        </button>
      </Card>
      <Card title="🏆 Experience Record">
        <Row label="Current NCC Project" value={w.ncc_site_name} />
        <Row label="Current Contractor" value={w.contractor_name} />
        <Row label="Registered Since" value={w.created_at ? new Date(w.created_at).toLocaleDateString() : null} />
      </Card>
      <Card title="🔄 Transfer History">
        {transfers.length === 0 ? (
          <p className="font-mono text-sm text-slate-500">No project transfers recorded — you have been on <b>{w.ncc_site_name || "your current project"}</b> since registration.</p>
        ) : transfers.map((tr, i) => (
          <Row key={i} label={tr.date || `Transfer ${i + 1}`} value={`${tr.from || "—"} → ${tr.to || "—"}${tr.reason ? ` (${tr.reason})` : ""}`} />
        ))}
      </Card>
    </>
  );
}

function SafetySection({ w }) {
  const perf = [
    { label: "Attendance %", v: w.attendance_pct },
    { label: "Productivity Rating", v: w.productivity_rating },
    { label: "Safety Rating", v: w.safety_rating },
    { label: "Training Completion %", v: w.training_completion_pct },
    { label: "Supervisor Feedback", v: w.supervisor_feedback },
  ];
  return (
    <>
      <Card title="🛡 Safety Compliance" testid="workman-safety">
        <Row label="PPE Issued" value={w.ppe_issued ? "YES" : "Not recorded"} />
        <Row label="PPE Expiry" value={w.ppe_expiry} />
        <Row label="Toolbox Talk Attendance" value={w.toolbox_talks_count != null ? `${w.toolbox_talks_count} session(s)` : "Not recorded"} />
        <Row label="Near Miss Reports" value={w.near_miss_count != null ? w.near_miss_count : "Not recorded"} />
        <Row label="Incident History" value={w.incident_history || "No incidents recorded"} />
        <p className="mt-3 font-mono text-[11px] text-slate-500">Safety compliance records are maintained by your site safety officer.</p>
      </Card>
      <Card title="📈 Performance Dashboard">
        {perf.map((p) => <Row key={p.label} label={p.label} value={p.v != null ? p.v : "Not yet rated"} />)}
        <p className="mt-3 font-mono text-[11px] text-slate-500">Performance ratings are updated by your supervisor / site HR.</p>
      </Card>
    </>
  );
}

function ReadySection({ w, t }) {
  const checks = [
    { label: "Aadhaar / ID Proof Verified", ok: !!(w.aadhaar || w.id_proof_number) },
    { label: "Bank Verified", ok: bankDone(w) },
    { label: "Medical Fit (Approved)", ok: w.medical_status === "approved" },
    { label: "Safety Induction Completed", ok: w.status === "approved" },
    { label: "Documents Verified", ok: !!w.photo_base64 },
    { label: "ID Card Generated", ok: !!w.id_card_no },
    { label: "Gate Pass Active", ok: w.is_active !== false },
  ];
  const ready = checks.every((c) => c.ok);
  return (
    <Card title="🎯 Ready to Mobilize Status" testid="workman-ready">
      <ul className="space-y-2">
        {checks.map((c) => (
          <li key={c.label} className="flex items-center justify-between border-b border-slate-100 pb-2 text-sm">
            <span>{c.label}</span>
            {c.ok ? <Check className="h-5 w-5 text-emerald-600" /> : <X className="h-5 w-5 text-red-500" />}
          </li>
        ))}
      </ul>
      <div className={`mt-4 border-2 p-3 text-center font-heading text-lg font-black ${ready ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-amber-500 bg-amber-50 text-amber-700"}`} data-testid="workman-ready-status">
        {ready ? `🟢 ${t("readyToWork")}` : `🟡 ${t("completePending")}`}
      </div>
    </Card>
  );
}
